// Chakra imports
import {
  Box,
  Grid,
  Input,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
// Custom components
import Card from 'components/card/Card.js';
import React from 'react';
import EditableInformation from './EditableInformation';
import Download from './Download';
import { getData } from '../../../../utils/httpClient';

export default function StudentData(props) {
  const { studentData, isEditing = false, onFieldChange } = props;
  const toast = useToast();
  const [accountSummary, setAccountSummary] = React.useState(null);

  // Chakra Color Mode
  const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = 'gray.400';
  const cardShadow = useColorModeValue(
    '0px 18px 40px rgba(112, 144, 176, 0.12)',
    'unset',
  );
  const editCardBg = useColorModeValue('white', 'navy.700');

  React.useEffect(() => {
    const sid = studentData?.student?.id;
    if (!sid) {
      setAccountSummary(null);
      return;
    }
    (async () => {
      try {
        const res = await getData(`/current-account/account/student/${sid}`);
        setAccountSummary(res?.payload?.account || null);
      } catch {
        setAccountSummary(null);
      }
    })();
  }, [studentData?.student?.id]);

  return (
    <Card mb={{ base: '0px', '2xl': '20px' }}>
      <Text
        color={textColorPrimary}
        fontWeight="bold"
        fontSize="2xl"
        mt="10px"
        mb="4px"
      >
        Datos escolares
      </Text>
      {accountSummary && (
        <Box mb={3}>
          <Text color={textColorSecondary} fontSize="sm">
            Cuenta corriente (saldo vigente)
          </Text>
          <Text color={textColorPrimary} fontWeight="700" fontSize="lg">
            ${Number(accountSummary?.balance ?? 0).toFixed(2)}
          </Text>
        </Box>
      )}
      <Grid
        templateColumns={{
          base: "1fr",
          lg: "1fr 1fr",
        }}
        templateRows={{
          base: "repeat(3, 1fr)",
          lg: "1fr",
        }}
        gap={{ base: "20px", xl: "20px" }}
        mt={{ base: "20px", lg: "20px" }}>
      <EditableInformation
        mb={2}
        boxShadow={cardShadow}
        title="Colegio"
        value={studentData?.student?.school}
        isEditing={isEditing}
        field="school"
        onFieldChange={(field, value) => {
          onFieldChange('student', {
            ...studentData?.student,
            [field]: value
          });
        }}
      />
      <EditableInformation
        mb={2}
        boxShadow={cardShadow}
        title="Orientación"
        value={studentData?.student?.orientation}
        isEditing={isEditing}
        field="orientation"
        onFieldChange={(field, value) => {
          onFieldChange('student', {
            ...studentData?.student,
            [field]: value
          });
        }}
      />
      <Box>
        {!isEditing ? (
          <Download
            mb={2}
            boxShadow={cardShadow}
            title="Formula 69A"
            bufferData={studentData?.student?.form_69A}
            fileName="test.pdf"
          />
        ) : (
          <Card bg={editCardBg} p={4} mb={2}>
            <Text fontWeight="500" color={textColorSecondary} fontSize="sm">
              Formula 69A (PDF)
            </Text>
            <Text color={textColorPrimary} fontWeight="500" fontSize="md" mt={1} mb={3}>
              {typeof studentData?.student?.form_69A === 'string'
                ? 'PDF cargado (nuevo)'
                : studentData?.student?.form_69A?.data?.length > 0
                  ? 'PDF disponible'
                  : 'No hay PDF cargado'}
            </Text>

            <Input
              type="file"
              accept="application/pdf"
              size="sm"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;

                if (file.type !== 'application/pdf') {
                  toast({
                    title: 'Archivo inválido',
                    description: 'Debe seleccionar un archivo PDF.',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                  });
                  return;
                }

                const maxSizeMb = 10;
                if (file.size > maxSizeMb * 1024 * 1024) {
                  toast({
                    title: 'Archivo muy grande',
                    description: `El PDF no debe superar ${maxSizeMb}MB.`,
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                  });
                  return;
                }

                const reader = new FileReader();
                reader.onloadend = () => {
                  const result = reader.result;
                  if (!result) return;
                  const dataUrl = String(result);
                  const base64 = dataUrl.split(',')[1];

                  onFieldChange('student', {
                    ...studentData?.student,
                    form_69A: base64,
                    form_69AName: file.name,
                  });
                };
                reader.readAsDataURL(file);
              }}
            />
          </Card>
        )}
      </Box>
      <EditableInformation
        mb={2}
        boxShadow={cardShadow}
        title="Expedida por"
        value={studentData?.student?.issuedBy}
        isEditing={isEditing}
        field="issuedBy"
        onFieldChange={(field, value) => {
          onFieldChange('student', {
            ...studentData?.student,
            [field]: value
          });
        }}
      />
      <EditableInformation
        mb={2}
        boxShadow={cardShadow}
        title="Último examen"
        value={studentData?.student?.lastExamDate}
        isEditing={isEditing}
        field="lastExamDate"
        type="date"
        onFieldChange={(field, value) => {
          onFieldChange('student', {
            ...studentData?.student,
            [field]: value
          });
        }}
      />
      <EditableInformation
        mb={2}
        boxShadow={cardShadow}
        title="Departamento"
        value={studentData?.student?.department}
        isEditing={isEditing}
        field="department"
        onFieldChange={(field, value) => {
          onFieldChange('student', {
            ...studentData?.student,
            [field]: value
          });
        }}
      />
      </Grid>
    </Card>
  );
}
